(() => {
  const normalizeOptionName = (name) => {
    const normalized = String(name || '').trim().toLocaleLowerCase().replace(/\s+/g, ' ');
    if (normalized === 'color' || normalized === 'colour') return 'color';
    if (normalized === 'size') return 'size';
    return normalized;
  };

  const normalizeOptionValue = (value) => String(value || '').trim().toLocaleLowerCase();

  const productOptionDefinitions = (productData) =>
    Array.from(productData?.options || []).map((option, index) => ({
      name: typeof option === 'string' ? option : option?.name,
      position: Number.parseInt(typeof option === 'string' ? index + 1 : option?.position || index + 1, 10),
    }));

  const variantOptionMap = (variant, definitions) => {
    const values = Array.isArray(variant?.options)
      ? variant.options
      : definitions.map((definition) => variant?.[`option${definition.position}`]);
    return new Map(
      definitions.map((definition, index) => [
        normalizeOptionName(definition.name),
        normalizeOptionValue(values[index]),
      ]),
    );
  };

  const chooseVariant = ({ productData, selectedOptions = {}, requestedVariantId = null }) => {
    const variants = Array.from(productData?.variants || []);
    if (variants.length === 0) return null;

    if (requestedVariantId) {
      const requestedVariant = variants.find((variant) => String(variant.id) === String(requestedVariantId));
      if (requestedVariant) return requestedVariant;
    }

    const definitions = productOptionDefinitions(productData);
    const normalizedSelections = new Map(
      Object.entries(selectedOptions)
        .map(([name, value]) => [normalizeOptionName(name), normalizeOptionValue(value)])
        .filter(([name, value]) => name && value),
    );
    const candidates = variants.map((variant, index) => ({
      index,
      variant,
      values: variantOptionMap(variant, definitions),
    }));
    const available = candidates.filter(({ variant }) => variant.available === true);
    if (available.length === 0) return variants[0];

    const colorValue = normalizedSelections.get('color');
    const sizeValue = normalizedSelections.get('size');
    const targetNames = new Set(definitions.map((definition) => normalizeOptionName(definition.name)));
    const otherSelections = Array.from(normalizedSelections.entries()).filter(
      ([name]) => name !== 'color' && name !== 'size' && targetNames.has(name),
    );
    const preferOtherSelections = (items) => {
      if (items.length === 0 || otherSelections.length === 0) return items[0] || null;
      const exactOther = items.find(({ values }) =>
        otherSelections.every(([name, value]) => values.get(name) === value),
      );
      return exactOther || items[0] || null;
    };

    if (colorValue && sizeValue && targetNames.has('color') && targetNames.has('size')) {
      const exactColorSize = available.filter(
        ({ values }) => values.get('color') === colorValue && values.get('size') === sizeValue,
      );
      const match = preferOtherSelections(exactColorSize);
      if (match) return match.variant;
    }

    if (colorValue && targetNames.has('color')) {
      const sameColor = available.filter(({ values }) => values.get('color') === colorValue);
      const match = preferOtherSelections(sameColor);
      if (match) return match.variant;
    }

    if (sizeValue && targetNames.has('size')) {
      const sameSize = available.filter(({ values }) => values.get('size') === sizeValue);
      const match = preferOtherSelections(sameSize);
      if (match) return match.variant;
    }

    const matchingOther = preferOtherSelections(
      available.filter(({ values }) => otherSelections.every(([name, value]) => values.get(name) === value)),
    );
    return (matchingOther || available[0]).variant;
  };

  const buildCanonicalUrl = ({
    locationHref,
    canonicalUrl,
    canonicalHandle,
    currentHandle,
    variantId,
  }) => {
    const nextUrl = new URL(locationHref);
    const canonicalProductUrl = new URL(canonicalUrl, nextUrl.origin);
    nextUrl.pathname = canonicalProductUrl.pathname;
    nextUrl.searchParams.delete('style');
    nextUrl.searchParams.delete('variant');
    nextUrl.searchParams.delete('section_id');
    nextUrl.searchParams.delete('option_values');
    if (currentHandle && currentHandle !== canonicalHandle) nextUrl.searchParams.set('style', currentHandle);
    if (variantId) nextUrl.searchParams.set('variant', variantId);
    return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
  };

  const buildSectionUrl = ({ productUrl, sectionId, variantId }) => {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.delete('style');
    url.searchParams.delete('option_values');
    url.searchParams.set('section_id', sectionId);
    if (variantId) url.searchParams.set('variant', variantId);
    else url.searchParams.delete('variant');
    return url.toString();
  };

  const captureSelectedOptions = (root) => {
    const selections = {};
    const partialHandoff =
      root?.dataset.scoreggiaHandoffState === 'partial' ||
      root?.dataset.scoreggiaHandoffState === 'resolving';
    root?.querySelectorAll('variant-selects input[data-option-name]:checked').forEach((input) => {
      if (partialHandoff && input.dataset.scoreggiaExplicit !== 'true') return;
      selections[normalizeOptionName(input.dataset.optionName)] = input.value;
    });
    root?.querySelectorAll('variant-selects select').forEach((select) => {
      const option = select.selectedOptions?.[0];
      if (partialHandoff && option?.dataset.scoreggiaExplicit !== 'true') return;
      const name = option?.dataset.optionName || select.name?.replace(/^options\[|\]$/g, '');
      if (name) selections[normalizeOptionName(name)] = option?.value || select.value;
    });
    return selections;
  };

  const readVariantId = (root) => {
    const input = root?.querySelector('product-form .product-variant-id, form[data-type="add-to-cart-form"] input[name="id"]');
    if (input?.value) return input.value;
    try {
      return JSON.parse(root?.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null')?.id || null;
    } catch {
      return null;
    }
  };

  window.ScoreggiaProductFamilyCore = {
    buildCanonicalUrl,
    buildSectionUrl,
    captureSelectedOptions,
    chooseVariant,
    normalizeOptionName,
    readVariantId,
  };

  if (customElements.get('scoreggia-product-family')) return;

  customElements.define(
    'scoreggia-product-family',
    class ScoreggiaProductFamily extends HTMLElement {
      connectedCallback() {
        if (this.lifecycleController) return;
        this.lifecycleController = new AbortController();
        this.requestController = null;
        this.requestSequence = 0;
        this.loadingSnapshot = null;
        this.handleClick = this.handleClick.bind(this);
        this.handlePopState = this.handlePopState.bind(this);
        this.addEventListener('click', this.handleClick, { signal: this.lifecycleController.signal });
        window.addEventListener('popstate', this.handlePopState, { signal: this.lifecycleController.signal });
        if (window.Shopify?.designMode) return;
        Promise.resolve().then(() => this.restoreInitialState());
      }

      disconnectedCallback() {
        this.lifecycleController?.abort();
        this.lifecycleController = null;
        this.requestController?.abort();
        this.requestController = null;
      }

      get productInfo() {
        return this.closest('product-info');
      }

      get styleOptions() {
        return Array.from(this.querySelectorAll('[data-scoreggia-style-option]'));
      }

      get activeOption() {
        return this.styleOptions.find((option) => option.getAttribute('aria-current') === 'true');
      }

      get statusRegion() {
        return this.querySelector('[data-scoreggia-family-status]');
      }

      findOption(handle) {
        return this.styleOptions.find((option) => option.dataset.productHandle === handle) || null;
      }

      findCanonicalOption() {
        return (
          this.styleOptions.find((option) => option.dataset.productId === this.dataset.canonicalProductId) ||
          this.styleOptions[0] ||
          null
        );
      }

      restoreInitialState() {
        if (!this.isConnected || !this.productInfo) return;
        if (document.documentElement.dataset.scoreggiaHandoff === 'true') return;
        if (
          this.productInfo.dataset.scoreggiaHandoffState === 'partial' ||
          this.productInfo.dataset.scoreggiaHandoffState === 'resolving'
        ) return;
        const url = new URL(window.location.href);
        const requestedHandle = url.searchParams.get('style');
        const requestedOption = requestedHandle ? this.findOption(requestedHandle) : null;
        const activeOption = this.activeOption;
        const activePath = activeOption ? new URL(activeOption.href, window.location.origin).pathname : '';
        const directChildVisit =
          !requestedHandle &&
          activeOption &&
          activeOption.dataset.productHandle !== this.dataset.canonicalProductHandle &&
          window.location.pathname === activePath;
        const targetOption = requestedOption || (directChildVisit ? activeOption : activeOption || this.findCanonicalOption());

        if (requestedOption && targetOption?.dataset.productHandle !== activeOption?.dataset.productHandle) {
          this.switchStyle(targetOption, {
            historyMode: 'replace',
            requestedVariantId: url.searchParams.get('variant'),
            restoreFocus: false,
          });
          return;
        }

        this.normalizeHistory('replace');
      }

      handlePopState() {
        if (window.Shopify?.designMode || !this.isConnected) return;
        const url = new URL(window.location.href);
        const requestedHandle = url.searchParams.get('style');
        const targetOption = requestedHandle ? this.findOption(requestedHandle) : this.findCanonicalOption();
        const safeTarget = targetOption || this.findCanonicalOption();
        if (!safeTarget) return;
        const requestedVariantId = url.searchParams.get('variant');
        const currentVariantId = readVariantId(this.productInfo);
        if (
          safeTarget.dataset.productHandle === this.activeOption?.dataset.productHandle &&
          (!requestedVariantId || String(requestedVariantId) === String(currentVariantId))
        ) {
          this.normalizeHistory('replace');
          return;
        }
        this.switchStyle(safeTarget, {
          historyMode: 'none',
          requestedVariantId,
          restoreFocus: false,
        });
      }

      handleClick(event) {
        const option = event.target.closest('[data-scoreggia-style-option]');
        if (
          !option ||
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          option.hasAttribute('download') ||
          (option.target && option.target !== '_self')
        ) {
          return;
        }

        event.preventDefault();
        if (option.dataset.productHandle === this.activeOption?.dataset.productHandle) {
          if (this.hasAttribute('data-loading')) {
            this.requestController?.abort();
            this.requestController = null;
            this.requestSequence += 1;
            this.setLoading(false);
          }
          option.focus({ preventScroll: true });
          if (
            this.productInfo.dataset.scoreggiaHandoffState !== 'partial' &&
            this.productInfo.dataset.scoreggiaHandoffState !== 'resolving'
          ) this.normalizeHistory('replace');
          return;
        }
        this.switchStyle(option, { historyMode: 'push', restoreFocus: true });
      }

      async switchStyle(option, { historyMode = 'push', requestedVariantId = null, restoreFocus = true } = {}) {
        if (!option || !this.productInfo) return;
        this.requestController?.abort();
        this.requestController = new AbortController();
        const { signal } = this.requestController;
        const sequence = ++this.requestSequence;
        const outgoingRoot = this.productInfo;
        const selectedOptions = captureSelectedOptions(outgoingRoot);
        const handoffSnapshot = window.ScoreggiaProductHandoff?.capture?.(outgoingRoot) || null;
        this.setLoading(true);

        try {
          const productData = await this.fetchProductData(option, signal);
          const selectedVariant = chooseVariant({ productData, selectedOptions, requestedVariantId });
          const response = await fetch(
            buildSectionUrl({
              productUrl: option.dataset.productUrl || option.href,
              sectionId: outgoingRoot.dataset.section,
              variantId: selectedVariant?.id || requestedVariantId,
            }),
            {
              cache: 'no-store',
              headers: { 'X-Requested-With': 'XMLHttpRequest' },
              signal,
            },
          );
          if (!response.ok) throw new Error('product family section request failed');
          const responseText = await response.text();
          if (sequence !== this.requestSequence || signal.aborted) return;
          const documentFragment = new DOMParser().parseFromString(responseText, 'text/html');
          const incomingRoot = documentFragment.querySelector('product-info');
          if (
            !incomingRoot ||
            incomingRoot.dataset.section !== outgoingRoot.dataset.section ||
            incomingRoot.dataset.productId !== option.dataset.productId ||
            incomingRoot.dataset.scoreggiaFamily !== 'true'
          ) {
            throw new Error('product family section response was invalid');
          }

          const finalVariantId = readVariantId(incomingRoot);
          let nextUrl = buildCanonicalUrl({
            locationHref: window.location.href,
            canonicalUrl: incomingRoot.dataset.scoreggiaCanonicalProductUrl,
            canonicalHandle: incomingRoot.dataset.scoreggiaCanonicalProductHandle,
            currentHandle: incomingRoot.dataset.scoreggiaCurrentStyleHandle,
            variantId: finalVariantId,
          });
          if (handoffSnapshot) {
            const featuredMedia =
              selectedVariant?.featured_media?.id ||
              selectedVariant?.featured_image?.id ||
              productData?.featured_media?.id ||
              null;
            handoffSnapshot.mediaId = featuredMedia;
            if (featuredMedia) {
              const partialUrl = new URL(option.dataset.productUrl || option.href, window.location.origin);
              if (selectedVariant?.id) partialUrl.searchParams.set('variant', String(selectedVariant.id));
              partialUrl.searchParams.set('sg_handoff', 'media');
              partialUrl.searchParams.set('sg_media', String(featuredMedia));
              partialUrl.searchParams.set('sg_options', JSON.stringify(handoffSnapshot.selections || {}));
              nextUrl = `${partialUrl.pathname}${partialUrl.search}${partialUrl.hash}`;
            }
          }
          this.updateHistory(historyMode, nextUrl);
          this.replaceProductInfo(
            outgoingRoot,
            incomingRoot,
            option.dataset.productHandle,
            restoreFocus,
            handoffSnapshot,
          );
        } catch (error) {
          if (error?.name === 'AbortError' || sequence !== this.requestSequence) return;
          this.setLoading(false);
          if (this.statusRegion) this.statusRegion.textContent = this.dataset.errorMessage || '';
          this.normalizeHistory('replace');
        }
      }

      async fetchProductData(option, signal) {
        try {
          const response = await fetch(option.dataset.productJsonUrl, {
            cache: 'no-store',
            headers: { Accept: 'application/json' },
            signal,
          });
          if (!response.ok) return null;
          const productData = await response.json();
          return Array.isArray(productData?.variants) ? productData : null;
        } catch (error) {
          if (error?.name === 'AbortError') throw error;
          return null;
        }
      }

      replaceProductInfo(outgoingRoot, incomingRoot, activeHandle, restoreFocus, handoffSnapshot = null) {
        outgoingRoot.querySelector('media-gallery')?.prepareForMediaMutation?.();
        const preparedRoot = incomingRoot.cloneNode(true);
        const loadedScripts = new Set(
          Array.from(document.querySelectorAll('script[src]')).map((script) => new URL(script.src, window.location.origin).href),
        );
        preparedRoot.querySelectorAll('script[src]').forEach((script) => {
          const source = new URL(script.src, window.location.origin).href;
          if (loadedScripts.has(source)) script.remove();
        });

        const wrapper = document.createElement('div');
        if (typeof HTMLUpdateUtility !== 'undefined') {
          HTMLUpdateUtility.setInnerHTML(wrapper, preparedRoot.outerHTML);
        } else {
          wrapper.innerHTML = preparedRoot.outerHTML;
        }
        const replacement = wrapper.firstElementChild;
        if (!replacement) throw new Error('product family replacement could not be prepared');
        outgoingRoot.replaceWith(replacement);
        if (handoffSnapshot) window.ScoreggiaProductHandoff?.apply?.(replacement, handoffSnapshot);
        try {
          window.Shopify?.PaymentButton?.init?.();
          window.ProductModel?.loadShopifyXR?.();
        } catch {}

        if (restoreFocus) {
          window.requestAnimationFrame(() => {
            Array.from(replacement.querySelectorAll('[data-scoreggia-style-option]'))
              .find((option) => option.dataset.productHandle === activeHandle)
              ?.focus({ preventScroll: true });
          });
        }
      }

      setLoading(loading) {
        const root = this.productInfo;
        if (!root) return;
        const submit = root.querySelector('.product-form__submit');
        if (loading) {
          if (!this.loadingSnapshot) {
            this.loadingSnapshot = {
              ariaDisabled: submit?.getAttribute('aria-disabled'),
              disabled: submit?.disabled,
            };
          }
          root.setAttribute('aria-busy', 'true');
          this.setAttribute('data-loading', '');
          if (this.statusRegion) this.statusRegion.textContent = '';
          if (submit) {
            submit.disabled = true;
            submit.setAttribute('aria-disabled', 'true');
          }
          return;
        }

        root.removeAttribute('aria-busy');
        this.removeAttribute('data-loading');
        if (submit && this.loadingSnapshot) {
          submit.disabled = Boolean(this.loadingSnapshot.disabled);
          if (this.loadingSnapshot.ariaDisabled === null) submit.removeAttribute('aria-disabled');
          else submit.setAttribute('aria-disabled', this.loadingSnapshot.ariaDisabled);
        }
        this.loadingSnapshot = null;
      }

      normalizeHistory(mode) {
        const root = this.productInfo;
        if (!root) return;
        if (
          root.dataset.scoreggiaHandoffState === 'partial' ||
          root.dataset.scoreggiaHandoffState === 'resolving'
        ) return;
        const nextUrl = buildCanonicalUrl({
          locationHref: window.location.href,
          canonicalUrl: root.dataset.scoreggiaCanonicalProductUrl,
          canonicalHandle: root.dataset.scoreggiaCanonicalProductHandle,
          currentHandle: root.dataset.scoreggiaCurrentStyleHandle,
          variantId: readVariantId(root),
        });
        this.updateHistory(mode, nextUrl);
      }

      updateHistory(mode, nextUrl) {
        const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (mode === 'push' && nextUrl !== currentUrl) {
          window.history.pushState(window.history.state, '', nextUrl);
        } else if (nextUrl !== currentUrl) {
          window.history.replaceState(window.history.state, '', nextUrl);
        }
      }
    },
  );
})();
